import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService } from '../services/api/auth/AuthService';
import { Apicadastro } from '../services/api/axioscadastro';
import { LocalizacaoService } from '../services/api/estoque/localizacao/LocalizacaoService';

interface IAuthContextData {
  logout: () => void;
  isAuthenticated: boolean;
  isSistema: boolean;
  login: (usuario: string, senha: string) => Promise<string | void>;
  sistema: (sistemas: string) => void;
  logoutsistema: () => void;
  isLoading: boolean;
}

const AuthContext = createContext({} as IAuthContextData);

const LOCAL_STORAGE_KEY__ACCESS_TOKEN = 'APP_ACCESS_TOKEN';

interface IAuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [accessSistema, setAccessSistema] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingLocations, setIsUpdatingLocations] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [filter, setFilter] = useState('');

  // 🔹 Recupera o token do localStorage sem usar JSON.parse
  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    if (token) setAccessToken(token);
    else setAccessToken(undefined);
  }, []);

  // 🔹 Login do usuário
  const handleLogin = useCallback(async (usuario: string, senha: string) => {
    setIsLoading(true);
    try {
      const result = await AuthService.auth(usuario, senha);
      if (result instanceof Error) throw new Error(result.message);

      // Salva token puro (sem JSON.stringify)
      localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, result.accessToken);
      setAccessToken(result.accessToken);

      // Atualiza cabeçalho padrão imediatamente
      Apicadastro.defaults.headers.Authorization = `Bearer ${result.accessToken}`;

      // 🔹 Exemplo de sincronização de localizações
      setIsUpdatingLocations(true);
      try {
        const getResult = await LocalizacaoService.getAll(pagina, filter, result.accessToken);
        console.log('Resultado da busca de localizações', getResult);

        if (!(getResult instanceof Error)) {
          const dadosParaEnviar = getResult.data.map(item => ({
            idlocalizacao: item.idlocalizacao,
            descricao: item.descricao,
          }));

          const createResult = await LocalizacaoService.create(dadosParaEnviar, result.accessToken);
          if (createResult instanceof Error) {
            console.error(createResult.message);
          } else {
            console.log('IDs criados:', createResult);
          }
        }
      } catch (error) {
        console.error('Erro durante fetch/create:', error);
      } finally {
        setIsUpdatingLocations(false);
      }

    } catch (error) {
      console.error('Erro no login:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pagina, filter]);

  // 🔹 Logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    setAccessToken(undefined);
    Apicadastro.defaults.headers.Authorization = null;
    setAccessSistema('');
  }, []);

  const handleLogoutsistema = useCallback(() => {
    setAccessSistema('');
  }, []);

  const handleSistema = useCallback((sistemas: string) => {
    setAccessSistema(sistemas);
  }, []);

  const isAuthenticated = useMemo(() => !!accessToken, [accessToken]);
  const isSistema = useMemo(() => !!accessSistema, [accessSistema]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isSistema,
        sistema: handleSistema,
        logoutsistema: handleLogoutsistema,
        login: handleLogin,
        logout: handleLogout,
        isLoading: isLoading || isUpdatingLocations,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
